/**
 * Utility functions to format brand data that may contain JSON strings
 * for better display in the UI.
 */

/**
 * Attempts to parse a JSON string and format it for human-readable display.
 * If the value is not valid JSON, returns it as-is.
 */
export function formatJsonField(value: string | undefined | null): string {
  if (!value) return '';

  // If it's already a plain string without JSON, return it
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return value;
  }

  try {
    const parsed = JSON.parse(trimmed);
    return formatParsedObject(parsed);
  } catch {
    // Not valid JSON, return as-is
    return value;
  }
}

/**
 * Recursively formats a parsed JSON object into a readable string.
 */
function formatParsedObject(obj: unknown, indent: number = 0): string {
  if (obj === null || obj === undefined) {
    return '';
  }

  if (typeof obj === 'string') {
    return obj;
  }

  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return String(obj);
  }

  if (Array.isArray(obj)) {
    return obj.map(item => formatParsedObject(item)).join(', ');
  }

  if (typeof obj === 'object') {
    const entries = Object.entries(obj);
    return entries
      .map(([key, value]) => {
        const formattedKey = formatKeyName(key);
        const formattedValue = formatParsedObject(value, indent + 1);
        return `${formattedKey}: ${formattedValue}`;
      })
      .join('\n');
  }

  return String(obj);
}

/**
 * Converts camelCase or snake_case keys to Title Case with spaces.
 */
function formatKeyName(key: string): string {
  return key
    // Split on camelCase
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // Split on snake_case
    .replace(/_/g, ' ')
    // Capitalize first letter of each word
    .replace(/\b\w/g, char => char.toUpperCase());
}

/**
 * Checks if a string value contains JSON that should be formatted.
 */
export function isJsonString(value: string | undefined | null): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return false;
  }
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
}

/**
 * Extracts a specific field from a JSON string.
 * Useful for getting individual values from structured data.
 */
export function extractFromJson(value: string | undefined | null, field: string): string {
  if (!value) return '';

  try {
    const parsed = JSON.parse(value.trim());
    if (typeof parsed === 'object' && parsed !== null && field in parsed) {
      const fieldValue = parsed[field];
      if (typeof fieldValue === 'string') {
        return fieldValue;
      }
      if (Array.isArray(fieldValue)) {
        return fieldValue.join(', ');
      }
      return formatParsedObject(fieldValue);
    }
  } catch {
    // Not valid JSON
  }

  return value;
}

/**
 * Converts a JSON string to a flat, human-readable format for form fields.
 * This version preserves more structure for editable contexts.
 */
export function flattenJsonForEdit(value: string | undefined | null): string {
  if (!value) return '';

  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return value;
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      // For objects, create a more readable flat string
      return Object.entries(parsed)
        .map(([key, val]) => {
          const formattedKey = formatKeyName(key);
          if (typeof val === 'string') {
            return `${formattedKey}: ${val}`;
          }
          if (Array.isArray(val)) {
            return `${formattedKey}: ${val.join(', ')}`;
          }
          return `${formattedKey}: ${JSON.stringify(val)}`;
        })
        .join('. ');
    }
    if (Array.isArray(parsed)) {
      return parsed.join(', ');
    }
  } catch {
    // Not valid JSON
  }

  return value;
}
